import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'
import { PageIdWoofzSmd } from './constants/pages'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const isNoAgressiveBehavior = () => {
  const agressiveBehaviorAnswer =
    useZustandSessionStore.getState().userAnswers?.agressive_behavior_smd ||
    useZustandGlobalStore.getState().lead?.quizzes?.agressive_behavior_smd
  return agressiveBehaviorAnswer === 'no'
}

const isDogActSame = () => {
  const dogActAnswer =
    useZustandSessionStore.getState().userAnswers?.dog_act_smd ||
    useZustandGlobalStore.getState().lead?.quizzes?.dog_act_smd
  return dogActAnswer === 'no'
}

export const getXStateConfigWoofzSmd = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const url = new URL(window.location.href)
          const pageId = url.pathname.split('/').pop()
          const isNewsletterPage = pageId === PageId.NEWSLETTER_URGE_SMD

          return !hasEmail && isNewsletterPage
        },
        target: pages.find((p) => p.pageId.includes('email'))?.pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !hasEmail &&
            !hasSubscription &&
            !hasAccount &&
            hasAnswers &&
            pageIdFromUrl !== PageIdWoofzSmd.SCRATCH_SCREEN_SMD
          )
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return pageIdFromUrl === PageIdWoofzSmd.SCRATCH_SCREEN_SMD
        },
        target: PageIdWoofzSmd.EMAIL_SMD,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageIdWoofzSmd.DOWNLOAD_SMD,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageIdWoofzSmd.ACCOUNT_SMD,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageIdWoofzSmd.SUBSCRIPTIONS_SMD,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.AGRESSIVE_BEHAVIOR_SMD && {
            NEXT_FLOW_STEP: [
              {
                cond: isNoAgressiveBehavior,
                target: PageId.BEHAVIOR_ISSUES_SMD,
              },
              {
                target: PageId.TRIGGERS_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.BEHAVIOR_ISSUES_SMD && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: isNoAgressiveBehavior,
                target: PageId.AGRESSIVE_BEHAVIOR_SMD,
              },
              {
                target: PageId.URGE_2_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_ACT_SMD && {
            NEXT_FLOW_STEP: [
              {
                cond: isDogActSame,
                target: PageId.PREPARE_SCREEN_SMD,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_PROFILE_SMD && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: isDogActSame,
                target: PageId.DOG_ACT_SMD,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_SMD && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageIdWoofzSmd.ACCOUNT_SMD,
              },
              {
                target: PageId.CHAT_UPSELL_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_SMD && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageIdWoofzSmd.ACCOUNT_SMD,
              },
              {
                target: PageId.UPSELL_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_SMD && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return languageFromUrl === LOCALES.EN
                },
                target: PageId.UPSELL_FEEDING_GUIDE_SMD,
              },
              {
                target: PageIdWoofzSmd.ACCOUNT_SMD,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_SMD && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_SMD,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_SMD && {
            NEXT_FLOW_STEP: PageIdWoofzSmd.ACCOUNT_SMD,
          }),
        },
      },
    }),
    {},
  ),
  [PageIdWoofzSmd.ACCOUNT_SMD]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_SMD,
    },
  },
  [PageId.DOWNLOAD_SMD]: {
    type: 'final',
  },
})
